import { Col, Row, Space, Typography } from 'antd';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import styled from 'styled-components';
import { useThemeComponents } from '../../../../themes';
import useTranslatedString from '../../../../utilities/useTranslatedString';
import OrderSummary from '../Components/OrderSummary';
import { type MobilityApplicationStateWithExpired, useMobilityBookingLocation } from '../helper';

const Title = styled.div`
    font-size: 36px;
    font-weight: bold;
    text-align: center;
    margin-bottom: 30px;
`;

const SuccessfulMessage = styled.div`
    font-size: 20px;
    font-weight: bold;
    margin-bottom: 16px;
`;

const Description = styled.div`
    font-size: 16px;
    margin-bottom: 16px;
`;

const Location = styled.div`
    margin-bottom: 24px;
`;

const LocationContainer = styled.div`
    font-size: 16px;
`;

const LocationName = styled(LocationContainer)`
    font-weight: bold;
`;

const colSpan = { lg: 8, md: 12, sm: 24, xs: 24 };

export type ThankYouContentProps<T> = {
    title: string;
    subTitle: string;
    description: string;
    application: T;
    externalModelInfoBaseUrl?: string;
};
const ThankYouContent = <T extends MobilityApplicationStateWithExpired>({
    title,
    application,
    subTitle,
    description,
    externalModelInfoBaseUrl,
}: ThankYouContentProps<T>) => {
    const navigate = useNavigate();
    const { t } = useTranslation(['mobilityThankYou', 'mobilityOrderSummary']);
    const { Button } = useThemeComponents();

    const locationInfo = useMobilityBookingLocation(application);

    const translate = useTranslatedString();

    const onClickAddress = useCallback(() => {
        window.open(`https://${locationInfo.url}`, '_blank');
    }, [locationInfo]);

    const onClickExplore = useCallback(() => {
        if (application.vehicle.__typename === 'LocalVariant' && externalModelInfoBaseUrl) {
            const { identifier } = application.vehicle.model;

            globalThis.open(
                `https://${externalModelInfoBaseUrl}/middle-east/models/${identifier}/${identifier}-models/`,
                '_blank'
            );
        }
    }, [application, externalModelInfoBaseUrl]);

    return (
        <>
            <Title>{title}</Title>
            <Row gutter={[40, 24]} justify="center" style={{ marginBottom: '24px' }}>
                <Col className="paymentDiv" {...colSpan}>
                    <OrderSummary
                        application={application}
                        showGiftVoucher={!!application.giftVoucher}
                        showPromoCode={!!application.promoCode}
                    />
                </Col>
                <Col {...colSpan}>
                    <SuccessfulMessage>{subTitle}</SuccessfulMessage>
                    <Description>{description}</Description>
                    {application.mobilityBookingDetails.location.__typename === 'MobilityBookingLocationPickup' && (
                        <Location>
                            <Space direction="vertical" size={10}>
                                <LocationName>{locationInfo.name}</LocationName>
                                <LocationContainer>
                                    <Typography.Link onClick={onClickAddress} underline>
                                        {locationInfo.address}
                                    </Typography.Link>
                                </LocationContainer>
                                <LocationContainer>
                                    <div>
                                        {t('mobilityOrderSummary:locationInfo.phone', {
                                            value: locationInfo.phone,
                                        })}
                                    </div>
                                    <div>
                                        {t('mobilityOrderSummary:locationInfo.email', {
                                            email: locationInfo.email,
                                        })}
                                    </div>
                                </LocationContainer>
                            </Space>
                        </Location>
                    )}
                    {application.mobilityBookingDetails.location.__typename === 'MobilityBookingLocationHome' &&
                        application.dealer && (
                            <Location>
                                <Space direction="vertical" size={10}>
                                    <LocationName>{translate(application.dealer.legalName)}</LocationName>
                                    {application.dealer.contact.address?.defaultValue && (
                                        <LocationContainer>
                                            <Typography.Link onClick={onClickAddress} underline>
                                                {translate(application.dealer.contact.address)}
                                            </Typography.Link>
                                        </LocationContainer>
                                    )}
                                    {(application.dealer.contact.telephone || application.dealer.contact.email) && (
                                        <LocationContainer>
                                            {application.dealer.contact.telephone && (
                                                <div>
                                                    {t('mobilityOrderSummary:locationInfo.phone', {
                                                        value: [
                                                            application.dealer.contact.telephone?.prefix
                                                                ? `+${application.dealer.contact.telephone?.prefix}`
                                                                : '',
                                                            application.dealer.contact.telephone?.value ?? '',
                                                        ].join(' '),
                                                    })}
                                                </div>
                                            )}

                                            {application.dealer.contact.email && (
                                                <div>
                                                    {t('mobilityOrderSummary:locationInfo.email', {
                                                        email: application.dealer.contact.email,
                                                    })}
                                                </div>
                                            )}
                                        </LocationContainer>
                                    )}
                                </Space>
                            </Location>
                        )}

                    <Space direction="vertical" size={16} style={{ width: '100%' }}>
                        <Button onClick={() => navigate('..')} type="primary" block>
                            {t('mobilityThankYou:actions.continueBrowsing')}
                        </Button>
                        {externalModelInfoBaseUrl && (
                            <Button onClick={onClickExplore} type="tertiary" block>
                                {t('mobilityThankYou:actions.moreDetails')}
                            </Button>
                        )}
                    </Space>
                </Col>
            </Row>
        </>
    );
};

export default ThankYouContent;
